import moment from 'moment';

/**
 * ### Moment Formatter Builder
 * @author <pino0071@gmail.com> Jose Aburto
 */
export default class MomentFormatter {
  private separator: DateSeparator = '/';
  private instance: moment.Moment = moment();

  private constructor(inp?: moment.MomentInput, configs?: MomentConfigs) {
    this.separator = configs?.separator ?? '/';
    this.instance = moment(inp, configs?.strict);
  }

  public static from(inp?: moment.MomentInput, configs?: MomentConfigs): Omit<MomentFormatter, 'from'> {
    if (!moment(inp).isValid()) new MomentFormatter(undefined, configs);
    return new MomentFormatter(inp, configs);
  }

  public static isValid(inp: moment.MomentInput) {
    return moment(inp).isValid();
  }

  /**
   * Format: `HH:mm`
   *
   * Example: `00:00`
   */
  public shortTime(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).shortTime);
  }

  /**
   * Format: `HH:mm:ss`
   *
   * Example: `00:00:00`
   */
  fullTime(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).fullTime);
  }

  /**
   * Format: `h:mm:ss a`
   *
   * Example: `12:00:00 am`
   */
  fullTimeWithDetails(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).fullTimeWithDetails);
  }

  /**
   * Format: `MM-DD-YYYY`
   *
   * Example: `01-06-2023`
   */
  fullDate(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).fullDate);
  }

  /**
   * Format: `MMM ddd MM-DD-YYYY`
   *
   * Example: `Jan Mon 01/09/2023`
   */
  fullDateWithDetail(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).fullDateWithDetail);
  }

  /**
   * Format: `MM-DD-YYYY HH:mm`
   *
   * Example: `01/08/2023 00:00`
   */
  shortDateTime(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).shortDateTime);
  }

  /**
   * Format: `MM-DD-YYYY - HH:mm:ss`
   *
   * Example: `01/08/2023 00:00:00`
   */
  fullDateTime(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).fullDateTime);
  }

  /**
   * Format: `MMM dddd YYYY, h:mm:ss a`
   *
   * Example: `Jan Friday 2023, 12:00:00 am`
   */
  fullDateTimeWithDetail(): string {
    return this.instance.format(AVAILABLE_FORMATS(this.separator).fullDateTimeWithDetail);
  }

  getInstance(): moment.Moment {
    return this.instance;
  }
}

// Inspiration from hub
export const AVAILABLE_FORMATS = (separator: DateSeparator = '/') => ({
  shortTime: 'HH:mm',
  fullTime: 'HH:mm:ss',
  fullTimeWithDetails: 'h:mm:ss a',
  fullDate: `MM${separator}DD${separator}YYYY`,
  fullDateTimeWithDetail: 'MMM dddd YYYY, h:mm:ss a',
  shortDateTime: `MM${separator}DD${separator}YYYY HH:mm`,
  fullDateTime: `MM${separator}DD${separator}YYYY - HH:mm:ss`,
  fullDateWithDetail: `MMM ddd MM${separator}DD${separator}YYYY`,
});

export type DateSeparator = '-' | '/' | '.';

export interface MomentConfigs {
  strict?: boolean;
  /**
   * Use this config to define dates separator `-`, `/` or `.`
   * @default '/'
   */
  separator?: DateSeparator;
}
